import { useMemo, useState } from 'react';

import styled from 'styled-components';

import { HorizontalTabs, HorizontalTabItem } from '@components/horizontal-tabs';
import type { IconProps } from '@components/icons';
import { IconAiAgent } from '@components/icons/ai-agent';
import { IconIntegration } from '@components/icons/integration';
import { IconIntegrations } from '@components/icons/integrations';
import { IconTrigger } from '@components/icons/trigger';
import { useIntegrationList } from '@components/integrations';
import { hideModal, Modal, showModal } from '@components/modal';
import { CloseButton } from '@components/modal/modal';
import { SearchBox } from '@components/search-box';
import { forceHideSidebar, hideSidebar } from '@components/sidebar';
import { Stack } from '@components/stack';
import { AI_GRADIENT } from '@components/text/text';
import { useScopedTranslate } from '@locale/translate';
import { theme } from '@styles/theme';
import { pxToRem } from '@utils/converters';

import { useAIAgents } from 'api/ai-agents/hooks';
import {
  useCreateJourneyAction,
  useInvalidateJourneyActions,
  useJourneyActionIsInsideLoop,
  useUpdateJourneyAction,
} from 'api/journey-actions/hooks';
import { BASE_ACTION_TYPE, JourneyType } from 'api/types';
import { actionConfig } from 'ui/actions/action-config';
import { useFeature } from 'ui/hooks';

import {
  ActionsTab,
  AIAgentsTab,
  AppsTab,
  CustomIntegrationsTab,
  OnCreateActionSelectProps,
} from './tabs';

import { EdgeProps } from '../base-builder';
import { hiddenIdentifier } from '../edge';
import { showActionSidebar } from '../show-action-sidebar';
import { setContinueRegistry } from '../unsaved-changes-prompt';

type AddActionModalProps = {
  journeyId: JourneyType['shortId'];
  edge: Pick<EdgeProps, 'id' | 'source' | 'target'>;
};

type ReplaceActionModalProps = {
  replaceActionId: string;
};

const openAddActionModal = (props: AddActionModalProps) => {
  forceHideSidebar();
  showModal({
    component: AddActionModal,
    options: { variant: 'LARGE' },
    props,
  });
};

export const showAddActionModal = (props: AddActionModalProps) => {
  const didHide = hideSidebar();
  if (didHide) {
    openAddActionModal(props);
  } else {
    setContinueRegistry({
      id: 'add-action-modal',
      onContinue: () => openAddActionModal(props),
    });
  }
};

const openReplaceActionModal = (props: ReplaceActionModalProps) => {
  forceHideSidebar();
  showModal({
    component: ReplaceActionModal,
    options: { variant: 'LARGE' },
    props,
  });
};

export const showReplaceActionModal = (props: ReplaceActionModalProps) => {
  const didHide = hideSidebar();
  if (didHide) {
    openReplaceActionModal(props);
  } else {
    setContinueRegistry({
      id: 'replace-action-modal',
      onContinue: () => openReplaceActionModal(props),
    });
  }
};

type ActionModalContentProps = {
  title: string;
  isLoading: boolean;
  loopCheckActionId?: string;
  onSelect: (props: OnCreateActionSelectProps) => void;
};

const ActionModalContent = ({
  title,
  isLoading,
  loopCheckActionId,
  onSelect,
}: ActionModalContentProps) => {
  const { t } = useScopedTranslate(locale);
  const [searchQuery, setSearchQuery] = useState('');
  const loweredSearchQuery = searchQuery?.toLowerCase();

  const { data: aiAgents, isLoading: isLoadingAgents } = useAIAgents();
  const integrations = useIntegrationList({ isTested: true });
  const { isEnabled: hasAskAIFeature } = useFeature('FEATURE_ASK_AI');
  const { isEnabled: hasAIAgentsFeature } = useFeature('FEATURE_AI_AGENTS');
  const { isEnabled: hasSMSFeature } = useFeature('FEATURE_SMS');
  const { isEnabled: hasCalendarActionFeature } = useFeature('FEATURE_CALENDAR_ACTION');
  const { isEnabled: hasLoopFeature } = useFeature('FEATURE_JOURNEY_LOOP');

  const loopCheckQuery = useJourneyActionIsInsideLoop(loopCheckActionId ?? '', {
    enabled: Boolean(loopCheckActionId),
  });
  const isInsideLoop = Boolean(loopCheckActionId) && loopCheckQuery.data;

  const skipTypes = useMemo<BASE_ACTION_TYPE[]>(() => {
    const types: BASE_ACTION_TYPE[] = [];
    if (!hasAskAIFeature) types.push('ask_ai');
    if (!hasSMSFeature) types.push('send_sms');
    if (!hasAIAgentsFeature) types.push('ask_ai_agent');
    if (!hasLoopFeature) types.push('journey_loop');
    if (isInsideLoop || !hasCalendarActionFeature) types.push('calendar_event');
    return types;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasAskAIFeature, hasSMSFeature, hasAIAgentsFeature, hasLoopFeature, isInsideLoop]);

  const skipGroups = isInsideLoop ? ['flowControl', 'wait'] : [];

  const filteredActionTypes = useMemo(
    () => getFilteredActionTypes(loweredSearchQuery, skipTypes),
    [loweredSearchQuery, skipTypes]
  );

  const actionsCount = loweredSearchQuery ? filteredActionTypes.length : undefined;

  const filteredAiAgents = aiAgents?.filter(
    (agent) => !loweredSearchQuery || agent.name.toLowerCase().includes(loweredSearchQuery)
  );

  const filteredIntegrations = integrations.data
    ?.filter((i) => i.endpoints.length > 0 || i.webhooks.length > 0)
    .map((integration) => {
      if (!loweredSearchQuery) {
        return integration;
      }

      const integrationNameMatches = integration.name.toLowerCase().includes(loweredSearchQuery);

      if (integrationNameMatches) {
        return integration;
      }

      return {
        ...integration,
        endpoints: integration.endpoints.filter((e) =>
          e.name.toLowerCase().includes(loweredSearchQuery)
        ),
        webhooks: integration.webhooks.filter((w) =>
          w.name.toLowerCase().includes(loweredSearchQuery)
        ),
      };
    })
    .filter((integration) => integration.endpoints.length > 0 || integration.webhooks.length > 0);

  const integrationsCount =
    filteredIntegrations?.reduce(
      (count, integration) => count + integration.endpoints.length + integration.webhooks.length,
      0
    ) ?? 0;

  const handleClearSearch = () => setSearchQuery('');

  const tabs: HorizontalTabItem[] = [
    {
      label: t('actions'),
      icon: IconTrigger,
      tabCount: loweredSearchQuery ? actionsCount : undefined,
      tabCountHighlight: true,
      content: (
        <ActionsTab
          filteredActionTypes={filteredActionTypes}
          skipGroups={skipGroups}
          onSelect={onSelect}
          onClearSearch={handleClearSearch}
          isDisabled={isLoading}
        />
      ),
    },
    {
      label: t('apps'),
      icon: IconIntegrations,
      content: <AppsTab />,
    },
    {
      label: t('aiAgents'),
      icon: IconAiAgent,
      selectedIcon: (props: IconProps) => <IconAiAgent color="aiGradientTeal100" {...props} />,
      tabCount: loweredSearchQuery ? filteredAiAgents?.length : undefined,
      tabCountHighlight: true,
      content: (
        <AIAgentsTab
          aiAgents={filteredAiAgents}
          isLoading={isLoadingAgents}
          onSelect={onSelect}
          searchQuery={loweredSearchQuery}
          onClearSearch={handleClearSearch}
        />
      ),
    },
    {
      label: t('customIntegrations'),
      icon: IconIntegration,
      tabCount: loweredSearchQuery ? integrationsCount : undefined,
      tabCountHighlight: true,
      content: (
        <CustomIntegrationsTab
          integrations={filteredIntegrations}
          isLoading={integrations.isLoading}
          onSelect={onSelect}
          searchQuery={loweredSearchQuery}
          onClearSearch={handleClearSearch}
        />
      ),
    },
  ];

  return (
    <ModalWrapper>
      <Modal isLoading={isLoading}>
        <Modal.Header>
          <Stack orientation="horizontal" align="center" justify="space-between">
            <Modal.HeaderTitle title={title} />
            <Stack orientation="horizontal" align="center" space={2}>
              <SearchBox
                placeholder={t('searchPlaceholder')}
                value={searchQuery}
                onChange={setSearchQuery}
              />
              <CloseButton onClick={hideModal} />
            </Stack>
          </Stack>
        </Modal.Header>
        <HorizontalTabs items={tabs} />
      </Modal>
    </ModalWrapper>
  );
};

const AddActionModal = ({ edge, journeyId }: AddActionModalProps) => {
  const { t } = useScopedTranslate(locale);
  const createMutation = useCreateJourneyAction();
  const { invalidateJourneyActions } = useInvalidateJourneyActions();
  const isLeaf = edge.target.endsWith(hiddenIdentifier);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSelect = async ({ actionType, values }: OnCreateActionSelectProps) => {
    if (isSubmitting) return;
    setIsSubmitting(true);
    const newAction = await createMutation.mutateAsync({
      journey: journeyId,
      parentAction: edge.source,
      childActions: edge.target === 'hidden' ? [] : [edge.target],
      isAutomatic: true,
      action: actionType,
      ...(values && { values }),
    });
    await invalidateJourneyActions();
    hideModal();
    showActionSidebar({ id: newAction.shortId, type: actionType }, { isNewAction: true });
  };

  return (
    <ActionModalContent
      title={t('title')}
      isLoading={isSubmitting}
      loopCheckActionId={isLeaf ? undefined : edge.target}
      onSelect={handleSelect}
    />
  );
};

const ReplaceActionModal = ({ replaceActionId }: ReplaceActionModalProps) => {
  const { t } = useScopedTranslate(locale);
  const updateMutation = useUpdateJourneyAction(replaceActionId);
  const { invalidateJourneyActions } = useInvalidateJourneyActions();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSelect = async ({ actionType, values }: OnCreateActionSelectProps) => {
    if (isSubmitting) return;
    setIsSubmitting(true);
    const newAction = await updateMutation.mutateAsync({
      action: actionType,
      ...(values && { values }),
    });
    await invalidateJourneyActions();
    hideModal();
    showActionSidebar({ id: newAction.shortId, type: actionType });
  };

  return (
    <ActionModalContent
      title={t('replaceTitle')}
      isLoading={isSubmitting}
      loopCheckActionId={replaceActionId}
      onSelect={handleSelect}
    />
  );
};

const ModalWrapper = styled.div({
  '#modal': {
    padding: pxToRem(24),
  },
  '[role="tablist"]': {
    paddingLeft: pxToRem(40),

    'button:nth-child(3)': {
      '&[aria-selected="true"]': {
        '> div:first-child': {
          background: AI_GRADIENT,
          backgroundClip: 'text',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',

          '.tab-count-badge': {
            WebkitTextFillColor: 'initial',
          },
        },
        svg: {
          color: theme.color.elementPurpleInvert,
        },
      },
      '&[aria-selected="true"] > div:last-child': {
        background: AI_GRADIENT,
      },
    },
  },
});

const PRIMARY_ACTION_TYPES: BASE_ACTION_TYPE[] = [
  'combo_send_form',
  'send_email',
  'send_sms',
  'calendar_event',
  'create_document',
  'convert_document',
  'ask_ai',
  'code',
  'formatter',
  'extract_text',
  'anonymize_text',
  'placeholder',
  'journey_branch',
  'condition_v2',
  'add_to_journey',
  'journey_loop',
  'wait_until_relative_date',
  'wait_for_time',
  'wait_for_attribute_change',
  'journey_merge',
  'update_traveller',
  'update_fields',
];

const getFilteredActionTypes = (
  searchQuery: string | undefined,
  skipTypes: BASE_ACTION_TYPE[]
): BASE_ACTION_TYPE[] => {
  return PRIMARY_ACTION_TYPES.filter((type) => {
    if (skipTypes.includes(type)) {
      return false;
    }
    if (!searchQuery) {
      return true;
    }
    return actionConfig[type].label.toLowerCase().includes(searchQuery);
  });
};

const locale = {
  en: {
    title: 'Add action',
    replaceTitle: 'Convert placeholder',
    searchPlaceholder: 'Search...',
    actions: 'Actions',
    apps: 'Apps',
    aiAgents: 'AI Agents',
    customIntegrations: 'Custom integrations',
    marketplace: 'Marketplace',
  },
};
